// Convite para o changelog publicado pela landing. Cada versão abre uma vez:
// qualquer forma de fechar registra a versão atual como vista.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Brand } from "./brand.js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CHANGELOG_LAST_SEEN_KEY,
  CHANGELOG_PAGE_URL,
  fetchChangelog,
  type ChangelogRelease,
} from "./changelog-service.js";
import "./i18n.js";

export function WhatsNewDialog() {
  const { t } = useTranslation();
  const [release, setRelease] = useState<ChangelogRelease | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void fetchChangelog(controller.signal)
      .then((feed) => {
        if (localStorage.getItem(CHANGELOG_LAST_SEEN_KEY) === feed.latestVersion) return;
        setRelease(feed.releases[0] ?? null);
        setOpen(true);
      })
      .catch(() => {
        // A landing ou o storage indisponíveis nunca devem bloquear o produto.
      });
    return () => controller.abort();
  }, []);

  function markAsSeen() {
    if (release) {
      try {
        localStorage.setItem(CHANGELOG_LAST_SEEN_KEY, release.version);
      } catch {
        // Conteúdo opcional continua acessível mesmo sem storage.
      }
    }
    setOpen(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      markAsSeen();
      return;
    }
    setOpen(true);
  }

  if (!release) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Duas colunas: a arte fica DENTRO do card, não atrás do app inteiro. O
          véu volta a ser o neutro padrão — é um convite, não uma interrupção de
          tela cheia. `p-0`/`gap-0` desfazem o padding do card para a imagem
          encostar na borda; o padding volta só na coluna de texto. */}
      <DialogContent
        showCloseButton={false}
        className="grid-cols-1 gap-0 overflow-hidden p-0 sm:max-w-2xl sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]"
      >
        {/* Em telas estreitas a coluna vira uma faixa no topo: 14rem de imagem
            ao lado de um celular não deixaria largura para ler nada. */}
        <div className="relative flex h-32 items-center justify-center overflow-hidden sm:h-auto">
          {/* O blur pesado puxa os pixels da borda para dentro e deixaria uma
              moldura clara; `scale-125` empurra essa borda para fora do corte.
              Blur nesse tamanho é uma MÉDIA da região, e a média desta imagem é
              quase preta — `brightness`/`saturate` devolvem o violeta da aurora
              que o borrão dilui. Sem véu escuro por cima: o resultado já é
              escuro o bastante para a marca branca. */}
          <div
            aria-hidden
            className="absolute inset-0 scale-125 bg-[url('/new.png')] bg-cover bg-center blur-3xl brightness-150 saturate-150"
          />
          {/* Sobre o véu escuro a marca é sempre a variante clara, em qualquer
              tema — o fundo aqui não acompanha o tema do app. */}
          <Brand size="lg" className="relative text-white" />
        </div>

        <div className="flex flex-col gap-4 p-4">
          <DialogHeader>
            <DialogTitle>{release.title}</DialogTitle>
            <DialogDescription>{release.summary}</DialogDescription>
          </DialogHeader>

          <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
            {release.changes.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>

          {/* `rounded-bl-none` porque, com a imagem à esquerda, o canto inferior
              esquerdo do rodapé fica no MEIO do card, não na borda dele. */}
          <DialogFooter className="mt-auto sm:rounded-bl-none">
            <DialogClose asChild>
              <Button variant="outline">{t("whatsNew.notNow")}</Button>
            </DialogClose>
            <Button asChild>
              <a href={CHANGELOG_PAGE_URL} target="_blank" rel="noreferrer" onClick={markAsSeen}>
                {t("whatsNew.view")}
              </a>
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
